import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import type { ProfileTaste } from '@/types/taste';
import type { RatingDistribution } from '@/types/feed';
import React, { useState } from 'react';
import { View } from 'react-native';

/**
 * The profile "taste you trust" signal: one full-width, four-band segmented
 * bar showing how a person's ratings break down across the app's rating
 * ladder. Band widths are proportional to each band's share of the total, and
 * the colors reuse the exact rating-meter ladder (Skip It -> Okay -> Try It ->
 * Must Try) so the bar reads as the same language as the rest of the app.
 *
 * Each band is self-labeling via a three-tier fit ladder against its measured
 * pixel width: a wide segment shows its label AND count together (centered), a
 * medium segment shows just the count, and a narrow (or zero) segment shows
 * nothing rather than clip or overflow. There is no separate legend row — the
 * label lives inside the band it names, so position never has to carry the
 * band -> label mapping on its own.
 *
 * Empty state: a brand-new user (`total === 0`) renders nothing rather than a
 * blank or broken track.
 */

type BandKey = keyof RatingDistribution;

interface Band {
  key: BandKey;
  label: string;
  /** Rating-ladder color class, shared with the rating meters. */
  color: string;
  /**
   * Text color for the in-band label and count, contrast-checked (>=4.5:1) on
   * `color` — near-black on the light green/coral/gray bands, light on the
   * dark Skip It band.
   */
  ink: string;
}

// Ordered left -> right, matching the rating meter's ascending ladder.
const BANDS: readonly Band[] = [
  {
    key: 'notForMe',
    label: 'Skip It',
    color: 'bg-muted-foreground/25',
    ink: 'text-foreground',
  },
  {
    key: 'okay',
    label: 'Okay',
    color: 'bg-muted-foreground',
    ink: 'text-background',
  },
  {
    key: 'tryIt',
    label: 'Try It',
    color: 'bg-primary',
    ink: 'text-primary-foreground',
  },
  {
    key: 'mustTry',
    label: 'Must Try',
    color: 'bg-mustTry',
    ink: 'text-primary-foreground',
  },
];

interface TasteBarProps {
  taste?: ProfileTaste | null;
}

/** "Rating mix: 20% Skip It, 30% Okay, 40% Try It, 10% Must Try" */
function accessibilityLabel(distribution: RatingDistribution, total: number): string {
  const parts = BANDS.map((band) => {
    const pct = Math.round((distribution[band.key] / total) * 100);
    return `${pct}% ${band.label}`;
  });
  return `Rating mix: ${parts.join(', ')}`;
}

export function TasteBar({ taste }: TasteBarProps) {
  // Measured pixel width of the track; drives the "does the count fit?" test.
  const [trackWidth, setTrackWidth] = useState(0);

  // Robust empty state: no data yet, or a brand-new user with no ratings.
  if (!taste || taste.total === 0) {
    return null;
  }

  const { distribution, total } = taste;

  return (
    <View className="mb-6">
      <Text className="text-sm font-semibold text-foreground mb-2">Rating mix</Text>

      <View
        testID="taste-bar-track"
        accessible
        accessibilityRole="image"
        accessibilityLabel={accessibilityLabel(distribution, total)}
        onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
        className="flex-row h-6 rounded-md overflow-hidden bg-secondary"
      >
        {BANDS.map((band) => {
          const count = distribution[band.key];
          const bandPx = total > 0 && trackWidth > 0 ? (count / total) * trackWidth : 0;
          // Three-tier fit ladder against the segment's measured pixel width.
          // Conservative width estimates for text-xs glyphs plus breathing room.
          const digits = String(count).length;
          const neededCount = digits * 8 + 8;
          const combined = `${band.label} ${count}`;
          const neededFull = combined.length * 7 + 12;
          const showFull = count > 0 && bandPx >= neededFull;
          const showCount = count > 0 && bandPx >= neededCount;

          return (
            <View
              key={band.key}
              testID={`taste-band-${band.key}`}
              className={cn('items-center justify-center overflow-hidden', band.color)}
              style={{ flex: count }}
            >
              {showFull ? (
                <View className="flex-row items-center justify-center" style={{ gap: 4 }}>
                  <Text className={cn('text-xs font-medium', band.ink)}>{band.label}</Text>
                  <Text className={cn('text-xs font-bold', band.ink)}>{count}</Text>
                </View>
              ) : showCount ? (
                <Text className={cn('text-xs font-semibold', band.ink)}>{count}</Text>
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
}

export default TasteBar;
